import { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import { type CppInvoiceTransactionType, getCppInvoiceTransactions } from '~/services/LuxLoyalty';

import { downloadCsvFile } from '~/utils/csvDownload';

import { MAX_EXPORT_ROWS, fetchAllInvoiceTransactions } from './fetchAllTransactions';
import { type MonthRange } from './monthRange';
import { transactionCsvHeaders, transactionsToCsvRows } from './transactionsCsv';

interface ExportArgs {
  partnerId: string;
  partnerName: string;
  range: MonthRange;
  /** Optional type filter, mirroring the Transactions grid's own filter. */
  transactionType?: CppInvoiceTransactionType | '';
}

/**
 * Downloads every transaction for a partner and period as CSV.
 *
 * Shared by the Transactions tab and the Invoice tab so the activity statement is byte-identical
 * wherever it is taken from — the file attached to an invoice has to be the same document an admin
 * can pull from the grid.
 *
 * The endpoint is paginated, so this walks every page rather than exporting the rows on screen;
 * `progress` reports how far it has got, and a period beyond the row cap downloads what it has and
 * says so rather than writing a silently partial file.
 */
export function useExportTransactionsCsv() {
  const { enqueueSnackbar } = useSnackbar();
  const [progress, setProgress] = useState<string | null>(null);
  // The run currently in flight, so it can be abandoned when the component unmounts, the selection
  // moves, or another export supersedes it. Walking a large period is many sequential requests.
  const runRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    runRef.current?.abort();
    runRef.current = null;
    setProgress(null);
  }, []);

  // Abandon an in-flight walk on unmount rather than letting it run to completion unobserved.
  useEffect(() => () => runRef.current?.abort(), []);

  const exportCsv = useCallback(
    async ({ partnerId, partnerName, range, transactionType }: ExportArgs) => {
      // A second export supersedes the first; only the latest may report or download.
      runRef.current?.abort();
      const run = new AbortController();
      runRef.current = run;
      setProgress('Preparing export…');

      try {
        const result = await fetchAllInvoiceTransactions(
          { partnerId, ...range, ...(transactionType && { transactionType }) },
          {
            fetchPage: getCppInvoiceTransactions,
            signal: run.signal,
            onProgress: ({ fetched, total }) => {
              if (!run.signal.aborted) {
                setProgress(`Fetched ${fetched.toLocaleString()} of ${total.toLocaleString()} rows…`);
              }
            },
          },
        );

        // An abandoned run must not download a file or raise a toast over whatever replaced it.
        if (run.signal.aborted) {
          return;
        }

        if (result.transactions.length === 0) {
          enqueueSnackbar('No transactions to export for this partner and month', { variant: 'info' });
          return;
        }

        const filename = `${partnerName || 'partner'}_transactions_${range.start}_${range.end}.csv`.replace(
          /\s+/g,
          '_',
        );
        downloadCsvFile(
          transactionsToCsvRows(result.transactions, partnerName, result.currency),
          transactionCsvHeaders(result.currency),
          filename,
        );

        if (result.truncated) {
          enqueueSnackbar(
            `Export stopped at the ${MAX_EXPORT_ROWS.toLocaleString()} row limit — ${result.totalItems.toLocaleString()} rows match. Narrow the filter to export the rest.`,
            { variant: 'warning', autoHideDuration: 10000 },
          );
        } else {
          enqueueSnackbar(`Exported ${result.transactions.length.toLocaleString()} transactions`, {
            variant: 'success',
          });
        }
      } catch (exportError) {
        // Abandoning a run is a deliberate act, not a failure to report.
        if (!run.signal.aborted) {
          enqueueSnackbar(`Export failed: ${exportError instanceof Error ? exportError.message : 'unknown error'}`, {
            variant: 'error',
          });
        }
      } finally {
        // Only the latest run owns the progress line; a superseded one leaves it to its successor.
        if (runRef.current === run) {
          runRef.current = null;
          setProgress(null);
        }
      }
    },
    [enqueueSnackbar],
  );

  return { exportCsv, cancel, progress, isExporting: progress !== null };
}
